/**
 * existing.jsx
 *
 * The five original backend-backed games. Extracted out of GamesPage.jsx so
 * the page can group games into categories without duplicating logic.
 */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, Trophy, Clock, RotateCw, Send, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ====================================================================== */
/* 1. CARTA DEL DÍA                                                       */
/* ====================================================================== */
export const DailyCardGame = ({ authHeader }) => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const [history, setHistory] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [c, h] = await Promise.all([
        axios.get(`${API}/games/daily-card`, { headers: authHeader() }),
        axios.get(`${API}/games/daily-card/history`, { headers: authHeader() }),
      ]);
      setCard(c.data);
      setHistory(h.data || []);
      setRevealed(c.data?.already_drawn || false);
    } catch { /* noop */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-serif text-xl text-white mb-1">Tu carta de hoy</h2>
        <p className="text-xs text-neutral-400 mb-6">{new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</p>
        <div className="relative h-64 w-44 mx-auto cursor-pointer" onClick={() => setRevealed(true)} data-testid="daily-card-flipper">
          <AnimatePresence mode="wait">
            {!revealed ? (
              <motion.div
                key="back"
                initial={{ rotateY: 0 }}
                exit={{ rotateY: 180, opacity: 0 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#1a0a1f] via-[#0a0510] to-black border-2 border-[#d4af37]/40 flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.3)]"
              >
                <Sparkles className="w-16 h-16 text-[#d4af37]/60" />
              </motion.div>
            ) : (
              <motion.div
                key="front"
                initial={{ rotateY: 180, opacity: 0 }}
                animate={{ rotateY: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="absolute inset-0 rounded-xl bg-gradient-to-br from-amber-700/30 via-[#d4af37]/30 to-purple-900/30 border-2 border-[#d4af37] flex flex-col items-center justify-center p-4 text-center shadow-[0_0_60px_rgba(212,175,55,0.5)]"
              >
                <Sparkles className="w-10 h-10 text-[#d4af37] mb-2" />
                <div className="font-serif text-lg text-white">{card?.card?.name}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        {!revealed && <p className="text-xs text-neutral-400 mt-3">Toca la carta para revelar</p>}
        {revealed && card?.card?.msg && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-[#d4af37] italic mt-6 max-w-md mx-auto"
          >
            {`"${card.card.msg}"`}
          </motion.p>
        )}
      </div>

      {history.length > 1 && (
        <div className="mt-10">
          <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-3">Tus cartas anteriores</h3>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {history.slice(1, 16).map((h) => (
              <div key={h.date} className="bg-black/40 border border-white/10 rounded-lg p-2 text-center">
                <div className="text-[9px] text-neutral-500">{new Date(h.date).toLocaleDateString("es-MX", { day: "2-digit", month: "short" })}</div>
                <div className="text-[11px] text-white mt-1 leading-tight">{h.card?.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ====================================================================== */
/* 2. MEMORIA MÍSTICA                                                     */
/* ====================================================================== */
const LEVELS = [
  { id: "easy",   label: "Fácil",  pairs: 8,  cols: 4 },
  { id: "medium", label: "Medio",  pairs: 10, cols: 4 },
  { id: "hard",   label: "Difícil",pairs: 15, cols: 5 },
];

export const MemoryGame = ({ authHeader }) => {
  const [level, setLevel] = useState("easy");
  const [deck, setDeck] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [bestTimes, setBestTimes] = useState({});
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);

  const lvl = LEVELS.find((l) => l.id === level);

  const newGame = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/games/memory/deck/${level}`, { headers: authHeader() });
      setDeck(r.data?.deck || []);
      setFlipped([]); setMatched(new Set()); setMoves(0);
      setStartedAt(Date.now()); setElapsedMs(0);
    } catch { /* noop */ } finally { setLoading(false); }
  };

  useEffect(() => {
    axios.get(`${API}/games/memory/best`, { headers: authHeader() }).then((r) => setBestTimes(r.data || {})).catch(() => null);
    /* eslint-disable-next-line */
  }, []);
  useEffect(() => { newGame(); /* eslint-disable-next-line */ }, [level]);
  useEffect(() => {
    if (!startedAt) return undefined;
    timerRef.current = setInterval(() => setElapsedMs(Date.now() - startedAt), 100);
    return () => clearInterval(timerRef.current);
  }, [startedAt]);
  useEffect(() => {
    if (lvl && matched.size > 0 && matched.size === lvl.pairs) {
      clearInterval(timerRef.current);
      const finalTime = Date.now() - startedAt;
      axios.post(`${API}/games/memory/score`, { level, time_ms: finalTime, moves }, { headers: authHeader() })
        .then((r) => {
          if (r.data?.is_personal_best) toast.success(`🏆 ¡Nuevo récord personal! ${(finalTime/1000).toFixed(1)}s`);
          else toast.success(`Terminaste en ${(finalTime/1000).toFixed(1)}s`);
          axios.get(`${API}/games/memory/best`, { headers: authHeader() }).then((r2) => setBestTimes(r2.data || {})).catch(() => null);
        }).catch(() => null);
    }
    /* eslint-disable-next-line */
  }, [matched, lvl]);

  const onCardClick = (card) => {
    if (matched.has(card.id)) return;
    if (flipped.includes(card.slot_id)) return;
    if (flipped.length >= 2) return;
    const next = [...flipped, card.slot_id];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      const [a, b] = next.map((sid) => deck.find((c) => c.slot_id === sid));
      if (a.id === b.id) {
        setMatched((prev) => new Set([...prev, a.id]));
        setTimeout(() => setFlipped([]), 400);
      } else {
        setTimeout(() => setFlipped([]), 900);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1.5">
          {LEVELS.map((l) => (
            <button
              key={l.id}
              onClick={() => setLevel(l.id)}
              data-testid={`memory-level-${l.id}`}
              className={`text-[11px] px-3 py-1.5 rounded-full uppercase tracking-widest ${
                level === l.id ? "bg-[#d4af37] text-black font-bold" : "bg-black/40 border border-white/10 text-neutral-400 hover:text-white"
              }`}
            >{l.label}</button>
          ))}
        </div>
        <button onClick={newGame} data-testid="memory-restart" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-black/40 border border-white/10 text-neutral-300 hover:text-white">
          <RotateCw className="w-3.5 h-3.5" /> Nueva partida
        </button>
      </div>

      <div className="flex items-center justify-between text-xs text-neutral-300 px-1">
        <span><Clock className="w-3 h-3 inline" /> {(elapsedMs/1000).toFixed(1)}s</span>
        <span>Movimientos: {moves}</span>
        <span>Parejas: {matched.size}/{lvl?.pairs}</span>
        {bestTimes[level] && <span className="text-[#d4af37]"><Trophy className="w-3 h-3 inline" /> {(bestTimes[level].time_ms/1000).toFixed(1)}s</span>}
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin mx-auto" /></div>
      ) : (
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${lvl?.cols || 4}, minmax(0,1fr))` }}>
          {deck.map((card) => {
            const isFlipped = flipped.includes(card.slot_id) || matched.has(card.id);
            return (
              <button
                key={card.slot_id}
                onClick={() => onCardClick(card)}
                data-testid={`memory-card-${card.slot_id}`}
                className="relative aspect-[2/3] rounded-lg border border-[#d4af37]/30 overflow-hidden bg-gradient-to-br from-[#1a0a1f] to-black shadow-lg"
                style={{ perspective: 600 }}
              >
                <motion.div
                  animate={{ rotateY: isFlipped ? 180 : 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <div className="absolute inset-0 flex items-center justify-center" style={{ backfaceVisibility: "hidden" }}>
                    <Sparkles className="w-6 h-6 text-[#d4af37]/50" />
                  </div>
                  <div
                    className="absolute inset-0 flex items-center justify-center p-1 bg-gradient-to-br from-amber-700/30 via-[#d4af37]/30 to-purple-900/30 text-[10px] sm:text-xs text-center text-white font-serif leading-tight"
                    style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}
                  >
                    {card.name}
                  </div>
                </motion.div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ====================================================================== */
/* 3. ORÁCULO SÍ/NO                                                       */
/* ====================================================================== */
export const OracleGame = ({ authHeader }) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const loadQuota = async () => {
    try {
      const r = await axios.get(`${API}/games/oracle/quota`, { headers: authHeader() });
      setRemaining(r.data?.remaining ?? 0);
    } catch { /* noop */ }
  };
  const loadHistory = async () => {
    try {
      const r = await axios.get(`${API}/games/oracle/history`, { headers: authHeader() });
      setHistory(r.data || []);
    } catch { /* noop */ }
  };

  useEffect(() => { loadQuota(); loadHistory(); /* eslint-disable-next-line */ }, []);

  const ask = async () => {
    if (!question.trim()) return;
    setSubmitting(true);
    setAnswer(null);
    try {
      const r = await axios.post(`${API}/games/oracle/ask`, { question }, { headers: authHeader() });
      setTimeout(() => {
        setAnswer(r.data?.answer);
        setRemaining(r.data?.remaining);
        loadHistory();
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "El espíritu está ocupado");
    } finally {
      setTimeout(() => setSubmitting(false), 1500);
    }
  };

  const moodCls = {
    yes: "text-emerald-300 border-emerald-400/40 bg-emerald-500/10",
    no: "text-red-300 border-red-400/40 bg-red-500/10",
    maybe: "text-amber-300 border-amber-400/40 bg-amber-500/10",
    silent: "text-neutral-400 border-neutral-500/40 bg-neutral-500/10",
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <motion.div
          animate={submitting ? { scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] } : { scale: 1, rotate: 0 }}
          transition={{ duration: 1.5, repeat: submitting ? Infinity : 0 }}
          className="w-40 h-40 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500/30 via-[#d4af37]/30 to-blue-500/30 border-2 border-[#d4af37]/40 flex items-center justify-center shadow-[0_0_60px_rgba(212,175,55,0.4)]"
        >
          <Sparkles className="w-20 h-20 text-[#d4af37]" />
        </motion.div>
        <p className="text-xs text-neutral-400">Te quedan <b className="text-[#d4af37]">{remaining ?? "—"}</b> preguntas hoy</p>
      </div>
      <div className="flex gap-2 max-w-md mx-auto">
        <Input
          data-testid="oracle-question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="¿...?"
          disabled={submitting || remaining === 0}
          className="bg-black/40 border-white/10 text-white"
          onKeyDown={(e) => e.key === "Enter" && ask()}
        />
        <button
          data-testid="oracle-ask"
          onClick={ask}
          disabled={submitting || !question.trim() || remaining === 0}
          className="bg-[#d4af37] hover:bg-[#e6c350] text-black font-bold px-4 rounded-lg disabled:opacity-50 flex items-center gap-1"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
      <AnimatePresence>
        {answer && (
          <motion.div
            key={answer.text}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className={`max-w-md mx-auto rounded-xl border p-5 text-center ${moodCls[answer.mood] || ""}`}
            data-testid="oracle-answer"
          >
            <p className="font-serif text-2xl">{answer.text}</p>
          </motion.div>
        )}
      </AnimatePresence>
      {history.length > 0 && (
        <div className="mt-8">
          <h3 className="text-xs uppercase tracking-widest text-neutral-400 mb-3">Tus últimas preguntas</h3>
          <div className="space-y-2">
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="bg-black/40 border border-white/10 rounded-lg p-3">
                <div className="text-xs text-neutral-300">{h.question}</div>
                <div className={`text-[11px] mt-1 ${h.answer?.mood === "yes" ? "text-emerald-300" : h.answer?.mood === "no" ? "text-red-300" : h.answer?.mood === "maybe" ? "text-amber-300" : "text-neutral-400"}`}>
                  → {h.answer?.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ====================================================================== */
/* 4. RUEDA DE LA SUERTE                                                  */
/* ====================================================================== */
export const WheelGame = ({ authHeader }) => {
  const [slices, setSlices] = useState([]);
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/games/wheel/slices`, { headers: authHeader() })
      .then((r) => setSlices(r.data || []))
      .finally(() => setLoading(false));
    /* eslint-disable-next-line */
  }, []);

  const spin = async () => {
    if (spinning || result) return;
    setSpinning(true);
    try {
      const r = await axios.get(`${API}/games/wheel/spin`, { headers: authHeader() });
      const idx = r.data?.slice_index ?? 0;
      const sliceAngle = 360 / Math.max(1, slices.length);
      const finalRotation = 5 * 360 + (360 - idx * sliceAngle) - sliceAngle / 2;
      setRotation(finalRotation);
      setTimeout(() => {
        setResult(r.data);
        setSpinning(false);
        if (r.data.already_spun) toast.info("Ya giraste hoy. Vuelve mañana por otra suerte.");
      }, 3500);
    } catch (e) {
      setSpinning(false);
      toast.error(e?.response?.data?.detail || "El espíritu de la ruleta descansa");
    }
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6 text-center" data-testid="wheel-game">
      <h2 className="font-serif text-xl text-white">Gira la Rueda</h2>
      <p className="text-xs text-neutral-400 -mt-2">Una vez al día. Tu fortuna te espera.</p>
      <div className="relative mx-auto" style={{ width: 280, height: 280 }}>
        <div className="absolute left-1/2 -top-2 -translate-x-1/2 w-0 h-0 z-10"
             style={{ borderLeft: "12px solid transparent", borderRight: "12px solid transparent", borderTop: "20px solid #d4af37" }} />
        <motion.div
          animate={{ rotate: rotation }}
          transition={{ duration: 3.4, ease: [0.16, 0.84, 0.44, 1] }}
          className="absolute inset-0 rounded-full border-4 border-[#d4af37] shadow-[0_0_60px_rgba(212,175,55,0.5)]"
          style={{ background: `conic-gradient(${slices.map((_, i) => {
            const colors = ["#7c2d12", "#581c87", "#1e3a8a", "#065f46", "#7f1d1d", "#854d0e",
                            "#92400e", "#831843", "#0f766e", "#a16207", "#4c1d95", "#9f1239"];
            const angle = 360 / slices.length;
            return `${colors[i % colors.length]} ${i * angle}deg ${(i + 1) * angle}deg`;
          }).join(", ")})` }}
        >
          {slices.map((s, i) => {
            const angle = (360 / slices.length) * i + (360 / slices.length) / 2;
            return (
              <div
                key={s.id}
                className="absolute left-1/2 top-1/2 text-white text-[10px] font-semibold pointer-events-none"
                style={{ transform: `rotate(${angle}deg) translateY(-110px) rotate(${-angle}deg)`, transformOrigin: "0 0" }}
              >{s.label}</div>
            );
          })}
        </motion.div>
        <div className="absolute inset-0 flex items-center justify-center">
          <button
            onClick={spin}
            disabled={spinning || !!result}
            data-testid="wheel-spin"
            className="w-20 h-20 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-600 text-black font-bold uppercase text-xs shadow-2xl disabled:opacity-60"
          >{spinning ? "..." : result ? "Hecho" : "Girar"}</button>
        </div>
      </div>
      <AnimatePresence>
        {result && (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto bg-gradient-to-br from-[#d4af37]/15 to-purple-900/15 border border-[#d4af37]/50 rounded-xl p-5"
            data-testid="wheel-result">
            <div className="font-serif text-2xl text-white">{result.slice?.label}</div>
            <p className="text-sm text-[#d4af37] italic mt-2">{result.slice?.msg}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ====================================================================== */
/* 5. TRIVIA ASTROLÓGICA                                                  */
/* ====================================================================== */
export const TriviaGame = ({ authHeader }) => {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bestScore, setBestScore] = useState(null);

  const start = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/games/trivia/start`, { headers: authHeader() });
      setQuestions(r.data?.questions || []);
      setIdx(0); setScore(0); setSelected(null); setDone(false); setStartedAt(Date.now());
    } finally { setLoading(false); }
  };

  const finalize = async (finalScore) => {
    setDone(true);
    try {
      const r = await axios.post(`${API}/games/trivia/submit`,
        { score: finalScore, total: questions.length, time_ms: Date.now() - startedAt },
        { headers: authHeader() });
      setBestScore(r.data?.best_score);
      if (r.data?.is_personal_best) toast.success(`🏆 ¡Nuevo récord! ${finalScore}/${questions.length}`);
    } catch { /* noop */ }
  };

  const pick = (ans) => {
    if (selected !== null) return;
    setSelected(ans);
    const q = questions[idx];
    const newScore = ans === q.correct ? score + 1 : score;
    if (ans === q.correct) setScore(newScore);
    setTimeout(() => {
      if (idx + 1 >= questions.length) finalize(newScore);
      else { setIdx((i) => i + 1); setSelected(null); }
    }, 1100);
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 text-[#d4af37] animate-spin mx-auto" /></div>;

  if (questions.length === 0) {
    return (
      <div className="text-center py-12" data-testid="trivia-start-view">
        <BookOpen className="w-12 h-12 text-[#d4af37] mx-auto mb-3" />
        <h2 className="font-serif text-xl text-white mb-2">Trivia Astrológica</h2>
        <p className="text-sm text-neutral-400 mb-5 max-w-sm mx-auto">5 preguntas sobre tarot, signos y mística. Tu mejor puntuación queda guardada.</p>
        <button onClick={start} data-testid="trivia-start" className="bg-[#d4af37] hover:bg-[#e6c350] text-black font-bold px-6 py-3 rounded-full uppercase tracking-widest text-sm">Comenzar</button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="text-center py-10" data-testid="trivia-done">
        <Trophy className="w-12 h-12 text-[#d4af37] mx-auto mb-3" />
        <div className="text-3xl text-white font-bold">{score}/{questions.length}</div>
        <p className="text-sm text-neutral-400 mt-2">Puntuación final</p>
        {bestScore !== null && <p className="text-xs text-[#d4af37] mt-1">Tu mejor: {bestScore}</p>}
        <button onClick={start} data-testid="trivia-again" className="mt-6 bg-[#d4af37] hover:bg-[#e6c350] text-black font-bold px-5 py-2.5 rounded-full text-xs uppercase tracking-widest">Jugar otra vez</button>
      </div>
    );
  }

  const q = questions[idx];
  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="trivia-active">
      <div className="text-center text-xs text-neutral-400">Pregunta {idx + 1} de {questions.length}</div>
      <div className="h-1 bg-black/40 rounded-full overflow-hidden">
        <div className="h-full bg-[#d4af37] transition-all" style={{ width: `${((idx + 1) / questions.length) * 100}%` }} />
      </div>
      <h3 className="font-serif text-xl text-white text-center mt-4">{q.q}</h3>
      <div className="grid grid-cols-1 gap-2 mt-4">
        {q.answers.map((a, i) => {
          const isCorrect = i === q.correct;
          const isSelected = selected === i;
          const showCorrect = selected !== null && isCorrect;
          const showWrong = selected !== null && isSelected && !isCorrect;
          return (
            <button
              key={i}
              onClick={() => pick(i)}
              disabled={selected !== null}
              data-testid={`trivia-ans-${i}`}
              className={`px-4 py-3 rounded-lg text-sm text-left border transition-all ${
                showCorrect ? "bg-emerald-500/20 border-emerald-400 text-emerald-200" :
                showWrong   ? "bg-red-500/20 border-red-400 text-red-200" :
                isSelected  ? "bg-[#d4af37]/15 border-[#d4af37] text-[#d4af37]" :
                              "bg-black/40 border-white/10 text-neutral-200 hover:border-[#d4af37]/40"
              } disabled:cursor-not-allowed`}
            >{a}</button>
          );
        })}
      </div>
    </div>
  );
};
